import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, BackrsUser, InfoModal } from '@backrs_ui/common-ui';

import { ReactComponent as BackrsLogomark } from '@backrs_ui/common-ui/src/assets/images/backrs-logomark.svg';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { isUnsupportedEnrollmentType } from '@backrs_ui/grow-ui/src/pages/AssessMyTrack/assessMyTrack.utils';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useFetchUserProfileInfoQuery } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { isSandboxStaff } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import BlueprintImage from './assets/getting-started-blueprint.svg';
import ExploreImage from './assets/getting-started-explore.svg';
import LifeReadinessImage from './assets/getting-started-life-readiness.svg';
import OnTrackImage from './assets/getting-started-on-track.svg';

const STRINGS = appStrings.modules.pageShell.gettingStartedModal;

type StepDefinition = Readonly<{
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  resolveRoute: (user: BackrsUser | null | undefined, isGuest: boolean) => string;
  isComplete: (user: BackrsUser | null | undefined) => boolean;
}>;

/** A null `route` means the step only describes where to go: inert, but not dimmed like a `locked` one. */
type GettingStartedStep = Readonly<{
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  route: string | null;
  locked: boolean;
  highlighted: boolean;
  completed: boolean;
}>;

// todo: replace with APP_ROUTE once routes are declared in a file that can be imported here without a circular dependency
const STEP_DEFINITIONS: readonly StepDefinition[] = [
  {
    id: 'blueprint',
    title: STRINGS.steps.blueprint.title,
    description: STRINGS.steps.blueprint.description,
    imageUrl: BlueprintImage,
    resolveRoute: (user, isGuest) =>
      isGuest ? '/demo/survey' : user?.has_blueprint ? '/my-blueprint' : '/survey',
    isComplete: (user) => !!user?.has_blueprint,
  },
  {
    id: 'life-readiness',
    title: STRINGS.steps.lifeReadiness.title,
    description: STRINGS.steps.lifeReadiness.description,
    imageUrl: LifeReadinessImage,
    resolveRoute: () => '/life-readiness',
    isComplete: (user) => !!user?.has_life_readiness,
  },
  {
    id: 'on-track',
    title: STRINGS.steps.onTrack.title,
    description: STRINGS.steps.onTrack.description,
    imageUrl: OnTrackImage,
    resolveRoute: () => '/are-you-on-track',
    isComplete: (user) => !!user?.has_track_assessment,
  },
  {
    id: 'explore',
    title: STRINGS.steps.explore.title,
    description: STRINGS.steps.explore.description,
    imageUrl: ExploreImage,
    resolveRoute: () => '/tools',
    isComplete: () => false,
  },
];

const SANDBOX_STEP_DEFINITIONS: readonly Readonly<{
  id: string;
  title: string;
  description: string;
}>[] = [
  { id: 'coaching-dashboard', ...STRINGS.sandboxSteps.dashboard },
  { id: 'student-profile', ...STRINGS.sandboxSteps.profile },
  { id: 'advising-suggestions', ...STRINGS.sandboxSteps.suggestions },
];

const computeSteps = (
  user: BackrsUser | null | undefined,
  isGuest: boolean,
  definitions: readonly StepDefinition[],
): readonly GettingStartedStep[] => {
  const completedFlags = definitions.map((s) => (isGuest ? false : s.isComplete(user)));
  const firstIncompleteIdx = completedFlags.findIndex((done) => !done);

  return definitions.map((s, i) => ({
    id: s.id,
    title: `${i + 1}. ${s.title}`,
    description: s.description,
    imageUrl: s.imageUrl,
    route: s.resolveRoute(user, isGuest),
    completed: completedFlags[i],
    locked: i > firstIncompleteIdx,
    highlighted: i === firstIncompleteIdx,
  }));
};

const computeSandboxSteps = (): readonly GettingStartedStep[] =>
  SANDBOX_STEP_DEFINITIONS.map((s, i) => ({
    ...s,
    title: `${i + 1}. ${s.title}`,
    route: null,
    completed: false,
    locked: false,
    highlighted: false,
  }));

type GettingStartedModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onAcknowledge?: () => void;
}>;

const getNextStepId = (steps: readonly GettingStartedStep[]): string => {
  const next = steps.find((s) => !s.locked && !s.completed);

  return (next ?? steps[steps.length - 1]).id;
};

export const GettingStartedModal = ({ open, onClose, onAcknowledge }: GettingStartedModalProps) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user.user);
  const { isGuest } = useAuth();
  const { data: profileData } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !user?.id,
  });
  const enrollmentTypes = profileData?.profile.academicEnrollmentTypes;
  const isStaffSandbox = isSandboxStaff();
  const steps = useMemo(() => {
    if (isStaffSandbox) return computeSandboxSteps();

    const definitions = isUnsupportedEnrollmentType(enrollmentTypes)
      ? STEP_DEFINITIONS.filter((s) => s.id !== 'on-track')
      : STEP_DEFINITIONS;

    return computeSteps(user, isGuest, definitions);
  }, [isStaffSandbox, user, isGuest, enrollmentTypes]);
  const [selectedId, setSelectedId] = useState<string>(() => getNextStepId(steps));

  useEffect(() => {
    if (open) {
      setSelectedId(getNextStepId(steps));
    }
  }, [open, steps]);

  const goToStep = (step: GettingStartedStep) => {
    if (step.route === null) return;

    onAcknowledge?.();
    onClose();
    navigate(step.route);
  };

  const handleGetStarted = () => {
    if (isStaffSandbox) {
      onClose();

      return;
    }

    const step = steps.find((s) => s.id === selectedId);

    if (step && !step.locked) {
      goToStep(step);
    }
  };

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      dialogSx={dialogSx}
      headerVisual={<BackrsLogomark width={42} height={42} />}
      headerContent={
        <Box sx={headerContentSx}>
          <Typography variant="text-md-semibold" sx={titleSx}>
            {STRINGS.title}
          </Typography>
          <Typography variant="text-sm-regular" sx={subtitleSx}>
            {STRINGS.subtitle}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodySx}>
          <Box sx={stepsContainerSx}>
            {steps.map((step) => (
              <StepCard
                key={step.id}
                step={step}
                onSelect={() => {
                  if (step.locked) return;

                  goToStep(step);
                }}
              />
            ))}
          </Box>
          <Box sx={footerSx}>
            {!isStaffSandbox && (
              <Button
                id="getting-started-skip"
                hierarchy="Secondary gray"
                size="lg"
                fullWidth
                onClick={onClose}
              >
                {STRINGS.skipButton}
              </Button>
            )}
            <Button
              id="getting-started-get-started"
              hierarchy="Primary"
              size="lg"
              fullWidth
              onClick={handleGetStarted}
            >
              {STRINGS.getStartedButton}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const StepCard = ({
  step,
  onSelect,
}: Readonly<{ step: GettingStartedStep; onSelect: () => void }>) => {
  const routed = step.route !== null;
  const interactive = routed && !step.locked;

  return (
    <Box
      id={`getting-started-step-${step.id}`}
      {...(routed && {
        role: 'button',
        tabIndex: interactive ? 0 : -1,
        'aria-disabled': !interactive,
        onClick: onSelect,
        onKeyDown: (e: KeyboardEvent) => {
          if (!interactive) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        },
      })}
      sx={stepCardSx({
        dimmed: step.locked,
        highlighted: step.highlighted,
        interactive,
        hasImage: !!step.imageUrl,
      })}
    >
      {step.imageUrl && <Box sx={stepImageSx(!step.locked, step.imageUrl)} />}
      <Box sx={stepTextSx}>
        <Typography sx={stepTitleSx}>{step.title}</Typography>
        <Typography sx={stepDescriptionSx}>{step.description}</Typography>
      </Box>
      {step.completed && !step.locked && (
        <>
          <Box sx={completedTriangleSx} />
          <Box sx={completedCheckSx}>
            <AppIcon
              iconName="Check"
              diameter="20px"
              color="Colors/Base/White"
              strokeWidth="bold"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const dialogSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    maxWidth: '629px',
  },
};

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  position: 'relative',
  zIndex: 1,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  position: 'relative',
  zIndex: 1,
});

const stepsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const stepCardSx =
  ({
    dimmed,
    highlighted,
    interactive,
    hasImage,
  }: Readonly<{
    dimmed: boolean;
    highlighted: boolean;
    interactive: boolean;
    hasImage: boolean;
  }>): SxProps<Theme> =>
  ({ backrs }) => {
    const borderColor = highlighted
      ? backrs.colors['Brand/600']
      : dimmed
        ? backrs.colors['Colors/Border/border-primary']
        : backrs.colors['Colors/Base/Black'];
    const borderWidth = highlighted ? '2px' : '1px';

    return {
      position: 'relative',
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'stretch',
      gap: 0,
      height: hasImage ? { xs: 'auto', sm: '134px' } : 'auto',
      borderRadius: backrs.radius['radius-xl'],
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      border: `${borderWidth} solid ${borderColor}`,
      cursor: interactive ? 'pointer' : 'default',
      transition: 'background-color 0.15s ease',
      overflow: 'hidden',
      ...(interactive && {
        '&:hover': {
          backgroundColor: backrs.colors['Gray/50'],
        },
        '&:active': {
          backgroundColor: backrs.colors['Colors/Background/bg-primary'],
          boxShadow: `0 0 0 2px ${backrs.colors['Colors/Base/White']}, 0 0 0 5px ${backrs.colors['Brand/500']}`,
        },
      }),
    };
  };

const stepImageSx =
  (enabled: boolean, imageUrl: string): SxProps<Theme> =>
  ({ backrs }) => {
    const dividerColor = enabled
      ? backrs.colors['Colors/Base/Black']
      : backrs.colors['Colors/Border/border-primary'];

    return {
      flexShrink: 0,
      width: { xs: '100%', sm: '200px' },
      height: { xs: '72px', sm: '134px' },
      opacity: enabled ? 1 : 0.5,
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      borderBottom: { xs: `1px solid ${dividerColor}`, sm: 'none' },
      borderRight: { xs: 'none', sm: `1px solid ${dividerColor}` },
    };
  };

const stepTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  paddingY: backrs.spacing['spacing-xl'],
  paddingX: backrs.spacing['spacing-xl'],
  flex: 1,
  minWidth: 0,
  justifyContent: 'center',
});

const stepTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  ...typography['text-md-semibold'],
  '@media (min-width: 600px)': typography['text-xl-semibold'],
});

const stepDescriptionSx: SxProps<Theme> = ({ backrs, typography }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  ...typography['text-xs-regular'],
  '@media (min-width: 600px)': typography['text-sm-regular'],
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
});

const completedTriangleSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '55px',
  height: '55px',
  background: `linear-gradient(to top right, transparent 50%, ${backrs.colors['Colors/Base/Black']} 50%)`,
  pointerEvents: 'none',
});

const completedCheckSx: SxProps<Theme> = {
  position: 'absolute',
  top: '6px',
  right: '6px',
  pointerEvents: 'none',
  lineHeight: 0,
};
